import { api } from '@/api/api.v2';
import request from '@/api';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import { prepareTemplate, prepareTemplateBody } from './utils/prepareTemplateColumns';

/*
    Общая модель для работы с шаблонами
*/

const _normalize = data => {
    return {
        byId: data,
        allIds: Object.keys(data).map(Number),
    };
};

const notEmpty = data => !isEmpty(data.byId) && !isEmpty(data.allIds);

const mainData = { byId: {}, allIds: [] };

const USER_KEY_DEFAULT_TEMPLATE_ID = 'gis:default-template-id';

const initialState = {
    /*
        В поле data хранится информация о шаблонах, которые пришли с сервера
    */
    data: mainData,
    isLoading: false,
    error: null,

    /*
        Идентификатор последнего примененного шаблона
    */
    activeTemplateId: null,
    /*
        Идентификатор шаблона по умолчанию
    */
    defaultTemplateId: null,
    /*
        Настройки кривых шаблонов, которые применялись
    */
    currentTemplateCurveSettings: {},
};

export const templatesModel = {
    name: 'templates',
    state: initialState,
    reducers: {
        setResponseData(state, payload) {
            const { templates, userDefaultTemplateId } = payload;

            const normalizeData = _normalize(templates);

            let defaultTemplateId;

            if (userDefaultTemplateId && normalizeData.allIds.includes(userDefaultTemplateId)) {
                defaultTemplateId = userDefaultTemplateId;
            } else {
                defaultTemplateId = normalizeData.allIds[0];
            }

            return {
                ...state,
                data: normalizeData,
                defaultTemplateId,
                activeTemplateId: defaultTemplateId,
                isLoading: false,
            };
        },
        startRequest(state) {
            return { ...state, isLoading: true, error: null };
        },
        setError(state, error) {
            return { ...state, isLoading: false, error, data: mainData };
        },
        setActiveTemplateId(state, activeTemplateId) {
            return { ...state, activeTemplateId };
        },
        setDefaultTemplateId(state, defaultTemplateId) {
            return { ...state, defaultTemplateId };
        },
        /*
            Добавление в начало списка шаблонов нового (созданного) шаблона
        */
        addTemplate(state, template) {
            const { data } = state;
            const { id } = template;

            const updatedEntities = Object.assign({}, { [id]: template }, data.byId);
            const updatedIds = [id, ...data.allIds];

            const updatedData = {
                byId: updatedEntities,
                allIds: updatedIds,
            };

            return { ...state, data: updatedData };
        },
        updateTemplateById(state, template) {
            const { data } = state;

            const updatedTemplatesById = { ...data.byId, [template.id]: template };
            const updatedData = { ...data, byId: updatedTemplatesById };
            return { ...state, data: updatedData };
        },
        /*
            Удаление шаблона из структуры по его id
        */
        deleteTemplate(state, templateId) {
            const { data } = state;

            const updatedEntities = omit(data.byId, [templateId]);
            const updatedIds = data.allIds.filter(id => id !== templateId);

            const updatedData = {
                byId: updatedEntities,
                allIds: updatedIds,
            };

            return { ...state, data: updatedData };
        },
        setCurvesSettings(state, curvesSettings) {
            return { ...state, currentTemplateCurveSettings: curvesSettings };
        },
    },
    effects: dispatch => ({
        /*
            Закрепить за пользователем шаблон по умолчанию
        */
        async setDefaultTemplateId(defaultTemplateId) {
            try {
                const payload = {
                    key: USER_KEY_DEFAULT_TEMPLATE_ID,
                    data: { id: defaultTemplateId },
                };

                await request.user.saveData(payload);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error:save_user_default_template', error);
            }
        },
        /*
            Инициализация шаблонов. Проходит в два этапа:
            1. Загрузка самих шаблонов;
            2. Получение id шаблона, который был выбран пользователем по умолчанию;
        */
        async initTemplates(_, rootState) {
            const { data } = rootState.templates;

            if (notEmpty(data)) return;

            try {
                const templates = await dispatch.templates.getTemplates();

                for (const tId of Object.keys(templates)) {
                    const preparedTemplate = prepareTemplate(templates[tId]);
                    if (preparedTemplate) {
                        templates[tId] = preparedTemplate;
                    }
                }

                const userDefaultTemplateId = await dispatch.templates?.getUserDefaultTemplateId();

                const payload = { templates, userDefaultTemplateId };

                dispatch.templates.setResponseData(payload);
            } catch (e) {
                dispatch.templates.setError(e);
            }
        },
        /*
            Получение шаблона по умолчанию, который был выбран пользователем (если таковой имеется)
        */
        async getUserDefaultTemplateId() {
            try {
                const response = await request.user.loadData(USER_KEY_DEFAULT_TEMPLATE_ID);
                const { data } = response;

                if (data) return data.id;
                return null;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error:get_user_default_template_id', error);
                return null;
            }
        },
        /*
            Запрос за пользовательскими шаблонами
        */
        async getTemplates() {
            this.startRequest();
            try {
                const result = await api.carottage.fetchTemplates();

                if (!result || isEmpty(result)) {
                    //throw Error('data is empty');
                    console.warn('data is empty');
                }

                return result;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('error:get_templates', error);
                this.setError(error);
            }
        },
        async createTemplate(payload, rootState) {
            const body = prepareTemplateBody(rootState, payload);
            const createdTemplate = prepareTemplate(await api.carottage.createTemplate(body));

            dispatch.templates.addTemplate(createdTemplate);
        },
        async updateTemplate(payload, rootState) {
            const body = prepareTemplateBody(rootState, payload);
            const updatedTemplate = prepareTemplate(await api.carottage.updateTemplate(body));

            dispatch.templates.updateTemplateById(updatedTemplate);
        },
        async deleteTemplateById(templateId) {
            await api.carottage.deleteTemplate(String(templateId));
            dispatch.templates.deleteTemplate(templateId);
        },
    }),
};
